'use client';

import { getOutlineCtaClasses } from '@/components/common/OutlineCtaLink';
import { BlogCategoryDocument } from '../../../../prismicio-types';
import {
  HORIZONTAL_GRID_CLASS,
  HorizontalResourceCard,
  PostData,
  ResourceLibraryShell,
} from './components';

interface HorizontalCardsProps {
  categories: BlogCategoryDocument[];
  initialPosts: PostData[];
  initialHasMore: boolean;
  initialCategory?: string;
  initialSearch?: string;
  searchPlaceholder: string;
  loadMoreLabel: string;
}

// Main content fills the page container; the filter column is nudged left with
// a small negative margin so it visually peeks past the container edge without
// pushing the cards off the page bounds.
const SECTION_CLASS =
  'bg-transparent px-5 py-16 md:px-10 lg:px-16 xl:px-20 2xl:px-24';
const CONTAINER_CLASS =
  'mx-auto flex max-w-[1280px] flex-col gap-6 lg:flex-row lg:gap-8';
const SIDEBAR_OFFSET_CLASS = 'lg:-ml-10 xl:-ml-14';

// Design-diff overrides for this variant only — smaller / lighter typography,
// a 5px filter panel radius, and the shared OutlineCtaLink look applied to the
// Load More button (left-aligned, 25px above the cards grid).
const FILTER_PANEL_CLASS =
  'rounded-[5px] border border-[#ccc] bg-white p-4 backdrop-blur-[21px]';
const FILTER_TYPE_LABEL_CLASS = 'text-sm font-light text-black';
const SEARCH_INPUT_CLASS =
  'flex-1 bg-transparent text-[18px] font-light text-black placeholder:font-light placeholder:text-black/50 focus:outline-none';
const SORT_LABEL_CLASS = 'text-sm font-light';
const LOAD_MORE_WRAPPER_CLASS = 'flex justify-start pt-[25px]';
const LOAD_MORE_BUTTON_CLASS = getOutlineCtaClasses('light');

/**
 * Two-column grid of horizontal cards (image on the left, content on the
 * right, bordered "Read more" pill button). Same filter / search / sort /
 * load-more behavior as the default; only the card layout differs.
 */
export default function HorizontalCards(props: HorizontalCardsProps) {
  return (
    <ResourceLibraryShell
      {...props}
      gridClassName={HORIZONTAL_GRID_CLASS}
      renderCard={(post) => (
        <HorizontalResourceCard key={post.id} post={post} />
      )}
      sectionClassName={SECTION_CLASS}
      containerClassName={CONTAINER_CLASS}
      sidebarClassName={SIDEBAR_OFFSET_CLASS}
      sidebarPanelClassName={FILTER_PANEL_CLASS}
      filterTypeLabelClassName={FILTER_TYPE_LABEL_CLASS}
      searchInputClassName={SEARCH_INPUT_CLASS}
      sortLabelClassName={SORT_LABEL_CLASS}
      loadMoreWrapperClassName={LOAD_MORE_WRAPPER_CLASS}
      loadMoreButtonClassName={LOAD_MORE_BUTTON_CLASS}
    />
  );
}
